import React from 'react';
import { DatePicker as AntDatePicker } from 'antd';
import { createUseStyles } from 'react-jss';
import { ThemeType } from 'src/theming/jssTypes';

const { RangePicker, MonthPicker, WeekPicker, YearPicker, TimePicker, QuarterPicker } = AntDatePicker;

export type DatePickerVariant = 'default' | 'secondary' | 'inverse';

export type DatePickerProps = React.ComponentPropsWithoutRef<typeof AntDatePicker> &
  Record<string, any> & {
    variant?: DatePickerVariant;
    className?: string;
    dropdownClassName?: string;
    status?: 'error' | 'warning';
  };

export type RangePickerProps = React.ComponentPropsWithoutRef<typeof RangePicker> &
  Record<string, any> & {
    variant?: DatePickerVariant;
    className?: string;
    dropdownClassName?: string;
    status?: 'error' | 'warning';
  };

export type MonthPickerProps = React.ComponentPropsWithoutRef<typeof MonthPicker> &
  Record<string, any> & {
    variant?: DatePickerVariant;
    className?: string;
    dropdownClassName?: string;
    status?: 'error' | 'warning';
  };

export type WeekPickerProps = React.ComponentPropsWithoutRef<typeof WeekPicker> &
  Record<string, any> & {
    variant?: DatePickerVariant;
    className?: string;
    dropdownClassName?: string;
    status?: 'error' | 'warning';
  };

interface PickerStylesParams {
  variant?: DatePickerVariant;
  status?: 'error' | 'warning';
  className?: string;
  dropdownClassName?: string;
}

interface PickerStylesReturn {
  combinedClassName: string;
  combinedDropdownClassName: string;
}

const useStyles = createUseStyles((theme: ThemeType) => {
  const createDatePickerStyles = (variant: DatePickerVariant = 'default') => {
    const bg = variant === 'default' ? theme.color.semantic.background.neutral.secondary : theme.color.semantic.brand.default.muted;
    const border = variant === 'default' ? theme.color.semantic.border.primary : theme.color.semantic.brand.default.subtle;

    return {
      width: '100%',
      height: '32px',
      backgroundColor: `${bg} !important`,
      borderColor: `${border} !important`,
      borderWidth: theme.border.width.sm,
      borderRadius: theme.radius.sm,
      padding: `${theme.spacing[0]} ${theme.spacing[4]}`,
      fontSize: theme.typography.fontSize.sm,
      fontWeight: theme.typography.fontWeight.regular,
      lineHeight: theme.typography.lineHeight.normal,
      transition: 'all 0.3s',

      '& .ant-picker-input > input': {
        color: theme.color.semantic.text.primary,
        fontSize: theme.typography.fontSize.sm,
        '&::placeholder': {
          color: theme.color.semantic.text.soft,
        },
      },
      '& .ant-picker-suffix': {
        color: theme.color.semantic.text.soft,
      },
      '& .ant-picker-clear': {
        backgroundColor: bg,
        color: theme.color.semantic.text.soft,
        '&:hover': {
          color: theme.color.semantic.text.primary,
        },
      },
      '& .ant-picker-separator': {
        color: theme.color.semantic.text.soft,
      },
      '& .ant-picker-active-bar': {
        backgroundColor: theme.color.semantic.brand.default.primary,
      },

      // Hover state
      '&:hover': {
        backgroundColor: `${bg} !important`,
        borderColor: `${theme.color.semantic.brand.default.soft} !important`,
      },

      // Focus state
      '&.ant-picker-focused': {
        backgroundColor: `${bg} !important`,
        borderColor: `${theme.color.semantic.brand.default.moderate} !important`,
        boxShadow: 'none !important',
      },

      // Disabled state
      '&.ant-picker-disabled': {
        backgroundColor: `${theme.color.semantic.background.neutral.subtle} !important`,
        borderColor: `${theme.color.semantic.border.primary} !important`,
        opacity: `${theme.opacity[70]} !important`,
        cursor: 'not-allowed',
        '& .ant-picker-input > input': {
          color: theme.color.semantic.text.soft,
          cursor: 'not-allowed',
          '&::placeholder': {
            color: theme.color.semantic.text.soft,
          },
        },
        '& .ant-picker-suffix': {
          color: theme.color.semantic.text.soft,
        },
      },

      // Size variants - Small
      '&.ant-picker-small': {
        height: '24px',
        padding: `${theme.spacing[0]} ${theme.spacing[3]}`,
        fontSize: theme.typography.fontSize.sm,
        '& .ant-picker-input > input': {
          fontSize: theme.typography.fontSize.sm,
        },
      },

      // Size variants - Large
      '&.ant-picker-large': {
        height: '40px',
        padding: `${theme.spacing[0]} ${theme.spacing[4]}`,
        fontSize: theme.typography.fontSize.md,
        '& .ant-picker-input > input': {
          fontSize: theme.typography.fontSize.md,
        },
      },

      // Status error
      '&.datepicker-status-error': {
        borderColor: `${theme.color.semantic.brand.danger.light} !important`,
        '&:hover': {
          borderColor: `${theme.color.semantic.brand.danger.light} !important`,
        },
        '&.ant-picker-focused': {
          borderColor: `${theme.color.semantic.brand.danger.light} !important`,
        },
      },

      // Status warning
      '&.datepicker-status-warning': {
        borderColor: `${theme.color.semantic.accent.orange.strong} !important`,
        '&:hover': {
          borderColor: `${theme.color.semantic.accent.orange.strong} !important`,
        },
        '&.ant-picker-focused': {
          borderColor: `${theme.color.semantic.accent.orange.strong} !important`,
        },
      },
    };
  };

  const createDropdownStyles = (variant: DatePickerVariant = 'default') => {
    const bg = variant === 'default' ? theme.color.semantic.background.neutral.secondary : theme.color.semantic.brand.default.muted;
    const border = variant === 'default' ? theme.color.semantic.border.primary : theme.color.semantic.brand.default.subtle;

    return {
      '& .ant-picker-panel-container': {
        backgroundColor: `${bg} !important`,
        borderRadius: `${theme.radius.sm} !important`,
        boxShadow: `${theme.shadow.md} !important`,
      },
      '& .ant-picker-panel': {
        backgroundColor: `${bg} !important`,
        borderColor: `${border} !important`,
      },
      '& .ant-picker-header': {
        color: `${theme.color.semantic.text.primary} !important`,
        borderBottomColor: `${border} !important`,
      },
      '& .ant-picker-header button': {
        color: `${theme.color.semantic.text.soft} !important`,
      },
      '& .ant-picker-header button:hover': {
        color: `${theme.color.semantic.text.primary} !important`,
      },
      '& .ant-picker-header-view button': {
        color: `${theme.color.semantic.text.primary} !important`,
        fontWeight: `${theme.typography.fontWeight.medium} !important`,
      },
      '& .ant-picker-header-view button:hover': {
        color: `${theme.color.semantic.brand.default.primary} !important`,
      },
      '& .ant-picker-body': {
        padding: `${theme.spacing[2]} !important`,
      },
      '& .ant-picker-content th': {
        color: `${theme.color.semantic.text.soft} !important`,
        fontWeight: `${theme.typography.fontWeight.medium} !important`,
        fontSize: `${theme.typography.fontSize.sm} !important`,
      },
      '& .ant-picker-cell': {
        color: `${theme.color.semantic.text.primary} !important`,
      },
      '& .ant-picker-cell:hover .ant-picker-cell-inner': {
        backgroundColor: `${theme.color.semantic.brand.default.muted} !important`,
      },
      '& .ant-picker-cell-in-view': {
        color: `${theme.color.semantic.text.primary} !important`,
      },
      '& .ant-picker-cell-disabled::before': {
        backgroundColor: `${bg} !important`,
      },
      '& .ant-picker-cell-disabled .ant-picker-cell-inner': {
        color: `${theme.color.semantic.text.soft} !important`,
      },
      '& .ant-picker-cell-today .ant-picker-cell-inner': {
        borderColor: `${theme.color.semantic.brand.default.primary} !important`,
      },
      '& .ant-picker-cell-today .ant-picker-cell-inner::before': {
        borderColor: `${theme.color.semantic.brand.default.primary} !important`,
      },
      '& .ant-picker-cell-in-range::before': {
        backgroundColor: `${theme.color.semantic.brand.default.muted} !important`,
      },
      '& .ant-picker-cell-selected .ant-picker-cell-inner': {
        backgroundColor: `${theme.color.semantic.brand.default.primary} !important`,
        color: `${theme.color.semantic.text.inverse} !important`,
      },
      '& .ant-picker-cell-range-start .ant-picker-cell-inner': {
        backgroundColor: `${theme.color.semantic.brand.default.primary} !important`,
        color: `${theme.color.semantic.text.inverse} !important`,
      },
      '& .ant-picker-cell-range-end .ant-picker-cell-inner': {
        backgroundColor: `${theme.color.semantic.brand.default.primary} !important`,
        color: `${theme.color.semantic.text.inverse} !important`,
      },
      '& .ant-picker-cell-range-hover::before': {
        borderColor: `${theme.color.semantic.brand.default.primary} !important`,
      },
      '& .ant-picker-cell-range-hover::after': {
        borderColor: `${theme.color.semantic.brand.default.primary} !important`,
      },
      '& .ant-picker-cell-range-start:not(.ant-picker-cell-range-start-single)::before, & .ant-picker-cell-range-end:not(.ant-picker-cell-range-end-single)::before': {
        backgroundColor: `${theme.color.semantic.brand.default.muted} !important`,
      },
      '& .ant-picker-cell-range-hover-start::before, & .ant-picker-cell-range-hover-end::before, & .ant-picker-cell-range-hover:not(.ant-picker-cell-in-range)::before, & .ant-picker-cell-in-range.ant-picker-cell-range-hover::before': {
        backgroundColor: `${theme.color.semantic.brand.default.muted} !important`,
      },
      '& .ant-picker-cell-range-hover-start::after, & .ant-picker-cell-range-hover-end::after, & .ant-picker-cell-range-hover::after': {
        borderColor: `${theme.color.semantic.brand.default.primary} !important`,
      },
      '& .ant-picker-cell-in-range.ant-picker-cell-range-hover-start .ant-picker-cell-inner::after, & .ant-picker-cell-in-range.ant-picker-cell-range-hover-end .ant-picker-cell-inner::after': {
        backgroundColor: `${theme.color.semantic.brand.default.soft} !important`,
      },
      '& .ant-picker-footer': {
        borderTopColor: `${border} !important`,
      },
      '& .ant-picker-today-btn': {
        color: `${theme.color.semantic.brand.default.primary} !important`,
      },
      '& .ant-picker-today-btn:hover': {
        color: `${theme.color.semantic.brand.default.soft} !important`,
      },
      '& .ant-picker-now-btn': {
        color: `${theme.color.semantic.brand.default.primary} !important`,
      },
      '& .ant-picker-now-btn:hover': {
        color: `${theme.color.semantic.brand.default.soft} !important`,
      },
      '& .ant-picker-ok button': {
        backgroundColor: `${theme.color.semantic.brand.default.primary} !important`,
        borderColor: `${theme.color.semantic.brand.default.primary} !important`,
        color: `${theme.color.semantic.text.inverse} !important`,
      },
      '& .ant-picker-ok button:hover': {
        backgroundColor: `${theme.color.semantic.brand.default.soft} !important`,
        borderColor: `${theme.color.semantic.brand.default.soft} !important`,
      },
      '& .ant-picker-time-panel': {
        borderLeftColor: `${border} !important`,
      },
      '& .ant-picker-time-panel-column > li.ant-picker-time-panel-cell .ant-picker-time-panel-cell-inner': {
        color: `${theme.color.semantic.text.primary} !important`,
      },
      '& .ant-picker-time-panel-column > li.ant-picker-time-panel-cell .ant-picker-time-panel-cell-inner:hover': {
        backgroundColor: `${theme.color.semantic.brand.default.muted} !important`,
      },
      '& .ant-picker-time-panel-column > li.ant-picker-time-panel-cell-selected .ant-picker-time-panel-cell-inner': {
        backgroundColor: `${theme.color.semantic.brand.default.muted} !important`,
        color: `${theme.color.semantic.text.primary} !important`,
      },
      '& .ant-picker-ranges .ant-picker-preset > .ant-tag-blue': {
        backgroundColor: `${theme.color.semantic.brand.default.muted} !important`,
        borderColor: `${theme.color.semantic.brand.default.primary} !important`,
        color: `${theme.color.semantic.brand.default.primary} !important`,
      },
      '& .ant-picker-decade-panel .ant-picker-cell-inner': {
        borderRadius: `${theme.radius.sm} !important`,
      },
      '& .ant-picker-year-panel .ant-picker-cell-inner': {
        borderRadius: `${theme.radius.sm} !important`,
      },
      '& .ant-picker-quarter-panel .ant-picker-cell-inner': {
        borderRadius: `${theme.radius.sm} !important`,
      },
      '& .ant-picker-month-panel .ant-picker-cell-inner': {
        borderRadius: `${theme.radius.sm} !important`,
      },
      '& .ant-picker-week-panel .ant-picker-cell-inner': {
        borderRadius: `${theme.radius.sm} !important`,
      },
      '& .ant-picker-decade-panel .ant-picker-cell-selected .ant-picker-cell-inner': {
        backgroundColor: `${theme.color.semantic.brand.default.primary} !important`,
        color: `${theme.color.semantic.text.inverse} !important`,
      },
      '& .ant-picker-year-panel .ant-picker-cell-selected .ant-picker-cell-inner': {
        backgroundColor: `${theme.color.semantic.brand.default.primary} !important`,
        color: `${theme.color.semantic.text.inverse} !important`,
      },
      '& .ant-picker-quarter-panel .ant-picker-cell-selected .ant-picker-cell-inner': {
        backgroundColor: `${theme.color.semantic.brand.default.primary} !important`,
        color: `${theme.color.semantic.text.inverse} !important`,
      },
      '& .ant-picker-month-panel .ant-picker-cell-selected .ant-picker-cell-inner': {
        backgroundColor: `${theme.color.semantic.brand.default.primary} !important`,
        color: `${theme.color.semantic.text.inverse} !important`,
      },
      '& .ant-picker-week-panel .ant-picker-cell-selected .ant-picker-cell-inner': {
        backgroundColor: `${theme.color.semantic.brand.default.primary} !important`,
        color: `${theme.color.semantic.text.inverse} !important`,
      },
      '& .ant-picker-decade-panel .ant-picker-cell:hover .ant-picker-cell-inner': {
        backgroundColor: `${theme.color.semantic.brand.default.muted} !important`,
      },
      '& .ant-picker-year-panel .ant-picker-cell:hover .ant-picker-cell-inner': {
        backgroundColor: `${theme.color.semantic.brand.default.muted} !important`,
      },
      '& .ant-picker-quarter-panel .ant-picker-cell:hover .ant-picker-cell-inner': {
        backgroundColor: `${theme.color.semantic.brand.default.muted} !important`,
      },
      '& .ant-picker-month-panel .ant-picker-cell:hover .ant-picker-cell-inner': {
        backgroundColor: `${theme.color.semantic.brand.default.muted} !important`,
      },
      '& .ant-picker-week-panel .ant-picker-cell:hover .ant-picker-cell-inner': {
        backgroundColor: `${theme.color.semantic.brand.default.muted} !important`,
      },
    };
  };

  const createInverseDatePickerStyles = () => ({
    width: '100%',
    height: '32px',
    backgroundColor: `${theme.color.semantic.background.neutral.secondary}${theme.alpha[5]} !important`,
    border: 'none !important',
    borderRadius: theme.radius.sm,
    padding: `${theme.spacing[0]} ${theme.spacing[4]}`,
    fontSize: theme.typography.fontSize.sm,
    fontWeight: theme.typography.fontWeight.regular,
    lineHeight: theme.typography.lineHeight.normal,
    transition: 'all 0.3s',
    color: `${theme.color.semantic.text.inverse} !important`,

    '& .ant-picker-input > input': {
      color: `${theme.color.semantic.text.inverse} !important`,
      fontSize: theme.typography.fontSize.sm,
      '&::placeholder': {
        color: `${theme.color.semantic.text.inverse}${theme.alpha[60]} !important`,
      },
    },
    '& .ant-picker-suffix': {
      color: `${theme.color.semantic.text.inverse} !important`,
      fontSize: theme.typography.fontSize.sm,
    },
    '& .ant-picker-clear': {
      backgroundColor: `${theme.color.semantic.background.neutral.inverse} !important`,
      color: `${theme.color.semantic.text.inverse} !important`,
    },
    '& .ant-picker-separator': {
      color: `${theme.color.semantic.text.inverse} !important`,
      fontSize: theme.typography.fontSize.sm,
    },
    '& .ant-picker-active-bar': {
      backgroundColor: theme.color.semantic.brand.default.primary,
    },

    '&:hover': {
      backgroundColor: `${theme.color.semantic.background.neutral.secondary}${theme.alpha[5]} !important`,
      borderColor: 'transparent !important',
    },
    '&.ant-picker-focused': {
      backgroundColor: `${theme.color.semantic.background.neutral.secondary}${theme.alpha[5]} !important`,
      borderColor: 'transparent !important',
      boxShadow: `${theme.shadow.none} !important`,
    },
    '&.ant-picker-disabled': {
      backgroundColor: `${theme.color.semantic.background.neutral.subtle} !important`,
      borderColor: `${theme.color.semantic.border.primary} !important`,
      opacity: `${theme.opacity[70]} !important`,
      cursor: 'not-allowed',
    },
  });

  const createInverseDropdownStyles = () => ({
    '& .ant-picker-panel-container': {
      backgroundColor: `${theme.color.semantic.background.neutral.inverse} !important`,
      borderRadius: `${theme.radius.sm} !important`,
    },
    '& .ant-picker-panel': {
      backgroundColor: `${theme.color.semantic.background.neutral.inverse} !important`,
      borderColor: `${theme.color.semantic.background.neutral.secondary}${theme.alpha[10]} !important`,
    },
    '& .ant-picker-header': {
      color: `${theme.color.semantic.text.inverse} !important`,
      borderBottomColor: `${theme.color.semantic.background.neutral.secondary}${theme.alpha[10]} !important`,
    },
    '& .ant-picker-header button, & .ant-picker-header-view button': {
      color: `${theme.color.semantic.text.inverse} !important`,
    },
    '& .ant-picker-content th': {
      color: `${theme.color.semantic.text.subtle} !important`,
    },
    '& .ant-picker-cell': {
      color: `${theme.color.semantic.text.subtle} !important`,
    },
    '& .ant-picker-cell-in-view': {
      color: `${theme.color.semantic.text.inverse} !important`,
    },
    '& .ant-picker-cell-disabled::before': {
      backgroundColor: `${theme.color.semantic.background.neutral.inverse} !important`,
    },
    '& .ant-picker-cell-disabled .ant-picker-cell-inner': {
      color: `${theme.color.semantic.text.subtle} !important`,
    },
    '& .ant-picker-cell:hover .ant-picker-cell-inner': {
      backgroundColor: `${theme.color.semantic.brand.default.primary}${theme.alpha[40]} !important`,
    },
    '& .ant-picker-cell-in-range::before, & .ant-picker-cell-in-view.ant-picker-cell-in-range::before': {
      backgroundColor: `${theme.color.semantic.brand.default.primary}${theme.alpha[20]} !important`,
    },
    '& .ant-picker-cell-range-start::before, & .ant-picker-cell-range-end::before': {
      backgroundColor: `${theme.color.semantic.brand.default.primary}${theme.alpha[20]} !important`,
    },
    '& .ant-picker-cell-in-view.ant-picker-cell-range-start .ant-picker-cell-inner, & .ant-picker-cell-in-view.ant-picker-cell-range-end .ant-picker-cell-inner, & .ant-picker-cell-in-view.ant-picker-cell-selected .ant-picker-cell-inner': {
      backgroundColor: `${theme.color.semantic.brand.default.primary} !important`,
      color: `${theme.color.semantic.text.inverse} !important`,
    },
    '& .ant-picker-cell-in-view.ant-picker-cell-today .ant-picker-cell-inner::before': {
      borderColor: `${theme.color.semantic.brand.default.primary} !important`,
    },
    '& .ant-picker-cell-range-start:not(.ant-picker-cell-range-start-single)::before, & .ant-picker-cell-range-end:not(.ant-picker-cell-range-end-single)::before': {
      backgroundColor: `${theme.color.semantic.brand.default.primary}${theme.alpha[20]} !important`,
    },
    '& .ant-picker-cell-range-hover-start::before, & .ant-picker-cell-range-hover-end::before, & .ant-picker-cell-range-hover:not(.ant-picker-cell-in-range)::before, & .ant-picker-cell-in-range.ant-picker-cell-range-hover::before': {
      backgroundColor: `${theme.color.semantic.brand.default.primary}${theme.alpha[20]} !important`,
    },
    '& .ant-picker-cell-in-range.ant-picker-cell-range-hover-start .ant-picker-cell-inner::after, & .ant-picker-cell-in-range.ant-picker-cell-range-hover-end .ant-picker-cell-inner::after': {
      backgroundColor: `${theme.color.semantic.brand.default.primary}${theme.alpha[20]} !important`,
    },
    '& .ant-picker-cell-range-hover-start::after, & .ant-picker-cell-range-hover-end::after, & .ant-picker-cell-range-hover::after': {
      borderColor: `${theme.color.semantic.brand.default.primary} !important`,
    },
    '& .ant-picker-footer': {
      backgroundColor: `${theme.color.semantic.background.neutral.inverse} !important`,
      borderTopColor: `${theme.color.semantic.background.neutral.secondary}${theme.alpha[10]} !important`,
    },
    '& .ant-picker-ranges .ant-picker-preset > .ant-tag-blue': {
      backgroundColor: `${theme.color.semantic.brand.default.primary} !important`,
      color: `${theme.color.semantic.text.inverse} !important`,
      borderColor: `${theme.color.semantic.accent.blue.muted} !important`,
    },
    '& .ant-picker-today-btn': {
      color: `${theme.color.semantic.brand.default.primary} !important`,
    },
  });

  return {
    defaultDatePicker: createDatePickerStyles('default'),
    secondaryDatePicker: createDatePickerStyles('secondary'),
    inverseDatePicker: createInverseDatePickerStyles(),
    defaultDatePickerDropdown: createDropdownStyles('default'),
    secondaryDatePickerDropdown: createDropdownStyles('secondary'),
    inverseDatePickerDropdown: createInverseDropdownStyles(),
  };
});

const usePickerStyles = ({
  variant = 'default',
  status,
  className = '',
  dropdownClassName = '',
}: PickerStylesParams): PickerStylesReturn => {
  const classes = useStyles();

  const variantClass = variant === 'secondary'
    ? classes.secondaryDatePicker
    : variant === 'inverse'
      ? classes.inverseDatePicker
      : classes.defaultDatePicker;
  const statusClass = status ? `datepicker-status-${status}` : '';
  const combinedClassName = `${variantClass} ${statusClass} ${className}`.trim();

  const dropdownVariantClass = variant === 'secondary'
    ? classes.secondaryDatePickerDropdown
    : variant === 'inverse'
      ? classes.inverseDatePickerDropdown
      : classes.defaultDatePickerDropdown;
  const combinedDropdownClassName = `${dropdownVariantClass} ${dropdownClassName}`.trim();

  return { combinedClassName, combinedDropdownClassName };
};

const DatePickerComponent: React.FC<DatePickerProps> = ({
  size = 'middle',
  variant = 'default',
  className = '',
  status,
  dropdownClassName = '',
  allowClear = true,
  ...rest
}) => {
  const { combinedClassName, combinedDropdownClassName } = usePickerStyles({
    variant,
    status,
    className,
    dropdownClassName,
  });

  return (
    <AntDatePicker
      size={size}
      {...rest}
      allowClear={allowClear}
      className={combinedClassName}
      dropdownClassName={combinedDropdownClassName}
    />
  );
};

export const DateRangePicker: React.FC<RangePickerProps> = ({
  size = 'middle',
  variant = 'default',
  className = '',
  status,
  dropdownClassName = '',
  ...rest
}) => {
  const { combinedClassName, combinedDropdownClassName } = usePickerStyles({
    variant,
    status,
    className,
    dropdownClassName,
  });

  return (
    <RangePicker
      size={size}
      className={combinedClassName}
      dropdownClassName={combinedDropdownClassName}
      {...rest}
    />
  );
};

export const DateMonthPicker: React.FC<MonthPickerProps> = ({
  size = 'middle',
  variant = 'default',
  className = '',
  status,
  dropdownClassName = '',
  ...rest
}) => {
  const { combinedClassName, combinedDropdownClassName } = usePickerStyles({
    variant,
    status,
    className,
    dropdownClassName,
  });

  return (
    <MonthPicker
      size={size}
      className={combinedClassName}
      dropdownClassName={combinedDropdownClassName}
      {...rest}
    />
  );
};

export const DateWeekPicker: React.FC<WeekPickerProps> = ({
  size = 'middle',
  variant = 'default',
  className = '',
  status,
  dropdownClassName = '',
  ...rest
}) => {
  const { combinedClassName, combinedDropdownClassName } = usePickerStyles({
    variant,
    status,
    className,
    dropdownClassName,
  });

  return (
    <WeekPicker
      size={size}
      className={combinedClassName}
      dropdownClassName={combinedDropdownClassName}
      {...rest}
    />
  );
};

export const DateYearPicker: React.FC<DatePickerProps> = ({
  size = 'middle',
  variant = 'default',
  className = '',
  status,
  dropdownClassName = '',
  ...rest
}) => {
  const { combinedClassName, combinedDropdownClassName } = usePickerStyles({
    variant,
    status,
    className,
    dropdownClassName,
  });

  return (
    <YearPicker
      size={size}
      className={combinedClassName}
      dropdownClassName={combinedDropdownClassName}
      {...rest}
    />
  );
};

export const DateTimePicker: React.FC<DatePickerProps> = ({
  size = 'middle',
  variant = 'default',
  className = '',
  status,
  dropdownClassName = '',
  ...rest
}) => {
  const { combinedClassName, combinedDropdownClassName } = usePickerStyles({
    variant,
    status,
    className,
    dropdownClassName,
  });

  return (
    <TimePicker
      size={size}
      className={combinedClassName}
      dropdownClassName={combinedDropdownClassName}
      {...rest}
    />
  );
};

export const DateQuarterPicker: React.FC<DatePickerProps> = ({
  size = 'middle',
  variant = 'default',
  className = '',
  status,
  dropdownClassName = '',
  ...rest
}) => {
  const { combinedClassName, combinedDropdownClassName } = usePickerStyles({
    variant,
    status,
    className,
    dropdownClassName,
  });

  return (
    <QuarterPicker
      size={size}
      className={combinedClassName}
      dropdownClassName={combinedDropdownClassName}
      {...rest}
    />
  );
};

export const DatePicker = DatePickerComponent as typeof DatePickerComponent & {
  RangePicker: typeof DateRangePicker;
  MonthPicker: typeof DateMonthPicker;
  WeekPicker: typeof DateWeekPicker;
  YearPicker: typeof DateYearPicker;
  TimePicker: typeof DateTimePicker;
  QuarterPicker: typeof DateQuarterPicker;
};

DatePicker.RangePicker = DateRangePicker;
DatePicker.MonthPicker = DateMonthPicker;
DatePicker.WeekPicker = DateWeekPicker;
DatePicker.YearPicker = DateYearPicker;
DatePicker.TimePicker = DateTimePicker;
DatePicker.QuarterPicker = DateQuarterPicker;

export default DatePicker;
